import { ItemEditContent } from "@eusoft/webapp-framework";
import { arrayItemsSource, formatCurrency, formatText, objectEditor, required, type IArrayEditorOptions } from "@eusoft/webapp-ui";
import { colorsSource, entityItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { executeAsync } from "../../services/DataService";
import type { Guid, ICreateProduct, ICreateProductFormat, IUpdateProduct } from "../../entities/Commands";
import type { IItemEditArgs } from "@eusoft/webapp-framework";


const units = [
    ["grams", "g"],
    ["hectogram", "hg"],
    ["kilogram", "kg"],
    ["slice", undefined],
    ["pieces", undefined],
    ["portion", undefined],
    ["liters", "l"],
    ["centiliters", "cl"],
]

const formats = [
    "bottle",
    "can",
    "tap",
    "glass",
    "unique",
    "small",
    "medium",
    "large",
]

const formatEditor = {
    newItemLabel: "add-format",
    itemView: a => `${a.name} (${formatCurrency(a.price)})`,
    editMode: "popup",
    itemEditor: _ => objectEditor(bldf => <>
        {bldf.autoComplete(a => a.name, {
            validators: [required],
            editor: {
                freeText: true,
                itemsSource: arrayItemsSource(
                    formats.map(a => formatText("fr-" + a) as string),
                    a => a
                )
            }
        })}
        {bldf.number(a => a.price, {
            validators: [required]
        })}
        {bldf.autoComplete(a => a.unit, {
            validators: [],
            editor: {
                freeText: true,
                itemsSource: arrayItemsSource(
                    units.map(a => formatText("un-" + a[0]) as string),
                    a => a
                )
            }
        })}
        {bldf.number(a => a.value, {
            label: "unit-value",
            validators: []
        })}
    </>, {
        commitMode: "manual-inplace",
        style: "vertical",
        inputField: { style: "filled" },
    })
} as IArrayEditorOptions<ICreateProductFormat>;

export interface ICreateProductArgs extends IItemEditArgs<ICreateProduct> {
    saleCircuitId: Guid;
    merchantId: Guid;
}

export interface IEditProductArgs extends IItemEditArgs<IUpdateProduct> {
    id: Guid;
}

export const EditProductPage = ItemEditContent.bulder<IUpdateProduct, IEditProductArgs>()

    .info({
        name: "product-edit",
        route: "/product/{id}",
    })

    .options(() => ({
        style: "panel",
        title: formatText("edit-item", "product")
    }))

    .feature(Authorize<IEditProductArgs>(bld =>
        bld.businessAccount()
            .resource("Product", "edit", bld.args.id)))

    .save(async value => {

        const res = await executeAsync("UpdateProduct", value);

        return true;
    })

    .load(async args => {

        const view = (await executeAsync("ListProduct", { ids: [args.id] })).items[0];

        args.value = {
            description: view.description,
            id: view.id,
            name: view.name,
            formats: view.formats,
            merchantId: view.merchant?.id,
            color: view.color
        }

        return true;
    })

    .editor(bld => <>

        {bld.text(a => a.name, {
            validators: [required]
        })}

        {bld.autoComplete(a => a.color, {
            editor: {
                itemsSource: colorsSource,
                freeText: true
            }
        })}

        {bld.text(a => a.description, {
            editor: {
                multiLine: true,
                rows: 3,
            },
            validators: []
        })}

        {bld.array(a => a.formats, {
            editor: formatEditor,
            validators: [required]
        })}

    </>, {
        commitMode: "manual",
        style: "vertical",
        inputField: { style: "filled" },
    })
    .asContent();



export const CreateProductPage = ItemEditContent.bulder<ICreateProduct, ICreateProductArgs>()

    .info({
        name: "product-create",
        route: "/product/create",
    })

    .options(() => ({
        style: "panel",
        title: formatText("create-item", "product")
    }))

    .feature(Authorize(bld => bld.businessAccount()
        .resource("Product", "create")))

    .save(async (value) => {

        const id = await executeAsync("CreateProduct", value);

        return true;
    })

    .load(async (args) => {

        args.value = {} as ICreateProduct;

        return true;
    })

    .editor(bld => <>

        {bld.singleSelector(a => a.merchantId, {
            validators: [required],
            label: "merchant",
            editor: {
                itemsSource: entityItemsSource("ListMerchant")
            }
        })}

        {bld.text(a => a.name, {
            validators: [required]
        })}

        {bld.autoComplete(a => a.color, {
            editor: {
                itemsSource: colorsSource,
                freeText: true
            }
        })}

        {bld.text(a => a.description, {
            editor: {
                multiLine: true,
                rows: 3,
            },
            validators: []
        })}

        {bld.array(a => a.formats, {
            editor: formatEditor,
            validators: [required]
        })}


    </>, {
        commitMode: "manual",
        style: "vertical",
        inputField: { style: "filled" },
    })
    .asContent();
